// ============================================================================
// POSIÇÕES PROCESSUAIS — fonte única de verdade
// ARQUIVO: src/config/PosicoesProcessuais.js
// ============================================================================
// Extraído de duas cópias divergentes que evoluíram separadas — uma em
// ProcurationEngine.jsx (mais ramos: Societário/Empresarial, Tributário,
// Animal, Mandado de Segurança; tinha "Fiel Depositário" em Cível/Penal) e
// outra em ContractEngine.jsx (Previdenciário/Administrativo/Família mais
// detalhados; não tinha "Fiel Depositário"). CadernoCockpit.jsx (aba
// Resumo → Partes) tinha uma terceira lista, pequena e solta, sem nenhuma
// posição de Direito Animal/Fiel Depositário — achado que motivou esta
// unificação (22/08/2026): sem "Fiel Depositário" aqui, não dava pra
// classificar corretamente uma parte que atua como depositária fiel de bens
// apreendidos no Caderno de Autos.
//
// Esta versão é a UNIÃO das três — nenhuma posição que já funcionava em
// qualquer uma das telas foi removida, só reorganizada por ramo e
// desduplicada. Consumida por ProcurationEngine.jsx, ContractEngine.jsx e
// CadernoCockpit.jsx (Partes do caderno).
// ============================================================================

export const RAMO_TAG_MAP = {
  Civil: "Cível",
  Penal: "Criminal",
  Trabalho: "Trabalhista",
  Previdenciario: "Previdenciário",
  SocietarioEmpresarial: "Societário / Empresarial",
  Administrativo: "Administrativo",
  Tributario: "Tributário",
  Familia: "Família",
  Animal: "Direito Animal",
  MandadoSeguranca: "Mandado de Segurança",
  ALL: "Genérico",
};

export const POSICOES_POR_RAMO = {
  ALL: [
    "Autor / Requerente",
    "Réu / Requerido",
    "Contratante",
    "Autor",
    "Réu",
    "Litisconsorte",
    "Assistente",
    "Parte",
    "Fiscal da Lei / Custos Legis (Ministério Público)",
  ],
  Civil: [
    "Autor / Requerente",
    "Exequente",
    "Réu / Requerido",
    "Executado",
    "Litisconsorte / Terceiro",
    "Corréu",
    "Interveniente",
    "Fiador / Avalista",
    "Fiel Depositário",
  ],
  Penal: [
    "Investigado / Indiciado",
    "Réu / Acusado",
    "Querelante",
    "Querelado",
    "Vítima / Ofendido",
    "Assistente de Acusação",
    "Autoridade Investigante / Requisitante (Ministério Público)",
    "Fiel Depositário",
  ],
  Trabalho: [
    "Reclamante",
    "Reclamada",
    "Responsável Solidário / Subsidiário",
  ],
  Previdenciario: [
    "Requerente",
    "Requerido",
    "Segurado / Dependente",
    "Pensionista",
    "INSS / Autarquia",
    "Empregador",
  ],
  SocietarioEmpresarial: [
    "Sócio",
    "Administrador",
    "Representante Legal",
    "Empresa / Sociedade",
  ],
  Administrativo: [
    "Impetrante / Requerente",
    "Autoridade Coatora / Requerido",
    "Impetrante",
    "Contribuinte / Administrado",
    "Servidor Autuado",
    "Impetrado / Autoridade Coatora",
    "Fazenda Pública / Órgão Autuador",
  ],
  Tributario: [
    "Contribuinte / Requerente",
    "Fazenda Pública / Requerido",
  ],
  Familia: [
    "Autor / Requerente",
    "Réu / Requerido",
    "Requerente / Acordante",
    "Requerido",
    "Alimentando (Credor)",
    "Alimentante (Devedor)",
    "Inventariante",
    "Herdeiro / Meeiro",
    "Fiscal da Lei / Custos Legis (Ministério Público)",
  ],
  Animal: [
    "Animal",
    "Guardião(ã)/Representante (em nome do animal) — Autor",
    "Guardião(ã)/Representante (em nome do animal) — Réu",
  ],
  MandadoSeguranca: [
    "Impetrante",
    "Autoridade Coatora / Impetrado",
  ],
};

// ============================================================================
// AUXILIARES DA JUSTIÇA — Art. 149 do CPC/2015 (verificado contra o texto
// literal do artigo, 22/08/2026): "São auxiliares da Justiça, além de
// outros cujas atribuições sejam determinadas pelas normas de organização
// judiciária, o escrivão, o chefe de secretaria, o oficial de justiça, o
// perito, o depositário, o administrador, o intérprete, o tradutor, o
// mediador, o conciliador judicial, o partidor, o distribuidor, o
// contabilista e o regulador de avarias."
//
// Categoria TRANSVERSAL (não amarrada a ramo do direito) — um depositário
// existe em Cível ou Penal igualmente. "Fiel Depositário" continua também
// dentro de Civil/Penal acima (retrocompatibilidade — não perder nada que
// já funciona hoje), mas o lugar juridicamente correto dele é aqui: ele
// NUNCA é "parte" (autor/réu) — é um auxiliar com um encargo (múnus)
// imposto pelo juízo. Achado real (22/08/2026): a Dra. Vanessa, fiel
// depositária num caso real do [PET_A], não era nem "parte" nem "coparte"
// — o sistema não tinha onde colocá-la corretamente até este ajuste.
// ============================================================================
export const POSICOES_AUXILIARES_JUSTICA = [
  "Depositário / Fiel Depositário",
  "Perito",
  "Administrador",
  "Intérprete",
  "Tradutor",
  "Mediador",
  "Conciliador Judicial",
  "Partidor",
  "Distribuidor",
  "Contabilista",
  "Regulador de Avarias",
];

// ============================================================================
// POLO PROCESSUAL — 4ª revisão (24/08/2026), taxonomia de 6 categorias
// aprovada pelo titular após pesquisa e validação (CPC art. 17/18, art.
// 149/159, tela real do PJe/TJMG). Consumido EXCLUSIVAMENTE por
// CadernoCockpit.jsx (aba Resumo → Partes) — NÃO importar em
// ContractEngine.jsx/ProcurationEngine.jsx: este é o eixo PROCESSUAL (CPC),
// distinto de `POSICOES_POR_RAMO` (eixo de qualificação contratual por
// ramo do direito), que continua sendo o único eixo usado por aqueles dois
// arquivos.
//
// As 5 primeiras são categorias PROCESSUAIS (têm assento formal no
// processo, mesmo quando não são "parte" em sentido estrito):
// 1-2. Polo Ativo / Polo Passivo — parte, sentido estrito (Art. 17/18 CPC).
// 3. Terceiro Interessado — não é parte, mas é categoria processual real
//    (Art. 119-124 CPC — intervenção de terceiros); só usar quando
//    confirmado na capa do processo, nunca como suposição.
// 4. Auxiliar da Justiça — não é parte (Art. 149/159 CPC: escrivão, oficial
//    de justiça, perito, DEPOSITÁRIO, administrador, intérprete, tradutor,
//    mediador, conciliador, partidor, distribuidor, contabilista, regulador
//    de avarias). Promovida de subtipo dentro de "Terceiro Interessado"
//    para categoria própria — achado real: a Dra. Vanessa, fiel
//    depositária do caso [PET_A], estava classificada como "Terceiro
//    Interessado" só porque não havia onde mais colocá-la; juridicamente
//    ela nunca foi terceira, é auxiliar com múnus imposto pelo juízo.
// 5. Vítima / Ofendido — só processo penal; categoria própria, distinta de
//    Terceiro Interessado (mesmo rótulo já usado em
//    `POSICOES_POR_RAMO.Penal`, agora também promovido a POLO quando o
//    processo é Criminal).
//
// A 6ª categoria ("Quem Defendemos" / objeto não-processual) NÃO entra
// neste array — ela é NÃO-jurídica, de controle interno, exclusiva para
// entidades sem personalidade jurídica que o escritório efetivamente
// representa (ex.: animal — STJ não reconhece personalidade jurídica a
// animal, então [PET_A] nunca pode estar em Polo/Terceiro/Auxiliar; ele é
// o objeto do processo/bem jurídico tutelado). Ver `POLO_NAO_PROCESSUAL`
// abaixo — sentinela usada só por CadernoCockpit.jsx para renderizar essa
// 6ª seção separadamente, fora do loop `POLO_PROCESSUAL.map(...)`.
//
// Histórico do que já foi tentado e revertido aqui (pra não repetir):
// 1ª tentativa — NATUREZA_PARTICIPACAO (Parte/Auxiliar da Justiça/Fiscal da
//   Lei/Terceiro Interessado, Art. 149+176-178 CPC): juridicamente correta,
//   mas não batia com a divisão real que a tela do Projudi usa.
// 2ª tentativa — POLO_PROCESSUAL com "Outros Participantes"/"Outros
//   Interessados": pegou o termo certo da ABA DE CADASTRO do PJe (onde
//   "Terceiro Interessado" é só uma OPÇÃO dentro desse balde maior), não a
//   divisão doutrinária real usada na EXIBIÇÃO do processo — confirmado
//   contra a tela real do Projudi (TJGO), que mostra "TERCEIRO
//   JURIDICAMENTE INTERESSADO" como seção própria, não dentro de um balde
//   "outros".
// 3ª tentativa — só 3 categorias (Polo Ativo/Passivo/Terceiro Interessado)
//   em pé de igualdade: batia com a tela real e a doutrina, mas misturava
//   Auxiliar da Justiça (nunca é parte nem terceiro) dentro de "Terceiro
//   Interessado" e não tinha onde colocar objeto não-processual (animal).
// 4ª (esta): 5 categorias processuais + 1 categoria não-processual à parte,
//   pesquisada e validada contra CPC art. 17/18, art. 149/159 e tela oficial
//   do PJe/TJMG (24/08/2026).
//
// Eixo INDEPENDENTE de "é nosso cliente ou não" (partiesRefs vs.
// partes.adversas) — um auxiliar da justiça (Depositário) pode estar em
// "Auxiliar da Justiça" e ser nosso cliente ao mesmo tempo (caso real:
// Dra. Vanessa, fiel depositária do [PET_A]).
// ============================================================================
export const POLO_PROCESSUAL = [
  "Polo Ativo",
  "Polo Passivo",
  "Terceiro Interessado",
  "Auxiliar da Justiça",
  "Vítima / Ofendido",
];

// ============================================================================
// POLO NÃO-PROCESSUAL — sentinela para a 6ª categoria ("Quem Defendemos").
// Decisão de schema (24/08/2026): NÃO criar campo novo no Firestore — a 6ª
// categoria reaproveita `partiesRefs` (mesmo mecanismo de sempre, incluindo
// o suporte a "Animal" já existente em `handleAddCoParteFromCrm`), só
// gravando este valor sentinela no campo `polo` já existente. Vantagens:
// zero campo novo, zero rota de salvamento nova, nada quebra do código que
// já lê/grava `partiesRefs`. `CadernoCockpit.jsx` renderiza esta categoria
// numa seção separada, filtrando por `polo === POLO_NAO_PROCESSUAL`, FORA
// do loop `POLO_PROCESSUAL.map(...)` (o valor de propósito não está no
// array acima, para nunca aparecer como uma 6ª linha dentro do loop
// processual).
// ============================================================================
export const POLO_NAO_PROCESSUAL = "__QUEM_DEFENDEMOS_NAO_PROCESSUAL__";
